import { z } from "zod";

/**
 * Data region registry — the canonical source of "where the data is from".
 *
 * Every chart, export, and query filter that displays a dataset's
 * timestamps must declare which region the data belongs to. The region
 * carries:
 *
 *   - `primaryTimezone`   : the IANA zone the data is *natively* bucketed in.
 *                           Used as the "data time" mode for chart axes
 *                           and as the default tooltip slot for data-kind
 *                           timestamps. For LATAM this is São Paulo —
 *                           Brazil is the largest panel and the rest of
 *                           the continent overlaps within ±2h.
 *   - `secondaryTimezones`: additional zones surfaced in tooltips so a
 *                           viewer in NYC can also see Lima / Mexico City
 *                           without a context switch.
 *
 * Today only LATAM exists. The shape is deliberately set up so that
 * adding "GCC" or "EUROPE" later is a one-line addition to the union
 * and a one-entry addition to DATA_REGIONS — no other code in the
 * module needs to change.
 *
 * Why a registry and not a per-dataset field on the API:
 *   - The frontend already knows which package is selected (UI state).
 *   - Pinning canonical zones in code keeps the chart bucketing stable
 *     across deploys; we never want a customer to refresh and see a
 *     different x-axis because the backend renamed a region.
 *   - When/if the backend ever needs to override a region's zone, it
 *     can return `dataset.timezone` and the chart formatter will prefer
 *     it. Today that's not needed.
 */

/**
 * Discriminated union of every data region the platform serves.
 * Adding a region is a one-line change here plus one entry in
 * DATA_REGIONS below.
 */
export type RegionId = "LATAM";

export interface RegionTimezone {
  /** IANA name. */
  tz: string;
  /** Short label rendered in tooltips ("Brazil", "Peru"). */
  label: string;
}

export interface DataRegion {
  /** Stable identifier — used in URLs, query params, persistence. */
  id: RegionId;
  /** Human-readable label ("Latin America"). */
  label: string;
  /**
   * The IANA zone the data is natively bucketed in. Chart axes in "data"
   * mode render in this zone; data-kind <Timestamp> uses it as primary.
   */
  primaryTimezone: string;
  /**
   * Extra zones surfaced in tooltips. Order matters — rendered top-to-bottom.
   * The primary zone is intentionally NOT duplicated here.
   */
  secondaryTimezones: ReadonlyArray<RegionTimezone>;
}

export const DATA_REGIONS: Record<RegionId, DataRegion> = Object.freeze({
  LATAM: Object.freeze({
    id: "LATAM",
    label: "Latin America",
    // São Paulo: largest panel + Brazil never observes DST since 2019,
    // so axis ticks stay stable year-round.
    primaryTimezone: "America/Sao_Paulo",
    secondaryTimezones: Object.freeze([
      { tz: "America/Lima", label: "Peru" },
      { tz: "America/Mexico_City", label: "Mexico" },
    ]) as ReadonlyArray<RegionTimezone>,
  }),
}) as Record<RegionId, DataRegion>;

/**
 * Look up a region by id. Throws on unknown id — `RegionId` is exhaustive
 * at compile time, so this only fires if a value crosses the type boundary
 * (e.g. a URL param). Callers that accept user input should validate first.
 */
export function getRegion(id: RegionId): DataRegion {
  const region = DATA_REGIONS[id];
  if (!region) {
    throw new Error(`Unknown data region: ${id}`);
  }
  return region;
}

/**
 * All region ids, useful for iteration (settings dropdowns, etc.).
 */
export const REGION_IDS: ReadonlyArray<RegionId> = Object.freeze(
  Object.keys(DATA_REGIONS) as RegionId[],
);

/**
 * Zod schema for validating untrusted `RegionId` input — URL params,
 * API responses, form submissions. Use this at the boundary; once a
 * value has cleared the schema you can pass it to `getRegion()` safely.
 *
 * Derived from `REGION_IDS` so adding a region to the registry
 * automatically widens the schema. The `as [RegionId, ...RegionId[]]`
 * cast asserts non-empty, which Zod requires for `z.enum()`.
 */
export const regionIdSchema = z.enum(REGION_IDS as [RegionId, ...RegionId[]]);
